import { useState, useEffect, useCallback } from "react";
import {
  CreditCard, Plus, Edit, Trash2, CheckCircle, AlertCircle,
  X, Users, TrendingUp, PlusCircle, MinusCircle, ChevronRight, History
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function PakketModal({ pakket, onClose, onSave, token }) {
  const isEdit = Boolean(pakket?.id);
  const [form, setForm] = useState({
    naam: pakket?.naam || "",
    credits: pakket?.credits || 10,
    prijs: pakket?.prijs || 25,
    beschrijving: pakket?.beschrijving || "",
    actief: pakket?.actief ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = "w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-600";
  const labelCls = "text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.naam || form.credits <= 0) { setError("Naam en credits zijn verplicht"); return; }
    setSaving(true); setError("");
    try {
      const url = isEdit ? `${API}/api/schedule/creditpakketten/${pakket.id}` : `${API}/api/schedule/creditpakketten`;
      const method = isEdit ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, credits: Number(form.credits), prijs: Number(form.prijs) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Opslaan mislukt");
      onSave();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-white/8">
          <h3 className="text-white font-bold">{isEdit ? "Pakket bewerken" : "Nieuw creditpakket"}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Pakketnaam *</label>
            <input value={form.naam} onChange={e => set("naam", e.target.value)} placeholder="bv. Starter 10 credits" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Credits *</label>
              <input type="number" value={form.credits} onChange={e => set("credits", e.target.value)} min={1} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Prijs (€)</label>
              <input type="number" value={form.prijs} onChange={e => set("prijs", e.target.value)} min={0} step={0.01} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Beschrijving</label>
            <input value={form.beschrijving} onChange={e => set("beschrijving", e.target.value)} placeholder="Optioneel" className={inputCls} />
          </div>
          <div className="flex items-center justify-between bg-[#1a1a1a] rounded-xl p-3">
            <span className="text-zinc-300 text-sm font-semibold">Actief (zichtbaar voor sporters)</span>
            <button type="button" onClick={() => set("actief", !form.actief)}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${form.actief ? "bg-[#C9A84C]" : "bg-zinc-700"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.actief ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold">Annuleren</button>
            <button type="submit" disabled={saving} data-testid="save-pakket-btn"
              className="flex-1 py-2.5 rounded-xl bg-[#C9A84C] text-black text-sm font-bold disabled:opacity-40">
              {saving ? "..." : isEdit ? "Bijwerken" : "Aanmaken"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function HandmatigCreditsModal({ sporter, onClose, onSave, token }) {
  const [credits, setCredits] = useState(10);
  const [omschrijving, setOmschrijving] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputCls = "w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-600";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!omschrijving) { setError("Omschrijving is verplicht"); return; }
    setSaving(true); setError("");
    try {
      const r = await fetch(`${API}/api/schedule/credits/handmatig`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sporter_id: sporter.id, credits: Number(credits), omschrijving }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Mislukt");
      onSave(d.nieuw_saldo);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-white/8">
          <div>
            <h3 className="text-white font-bold">Credits aanpassen</h3>
            <p className="text-zinc-500 text-xs mt-0.5">{sporter.name} · huidig: {sporter.saldo} cr.</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block">Credits (gebruik negatief om af te schrijven)</label>
            <input type="number" value={credits} onChange={e => setCredits(e.target.value)} className={inputCls} />
            <p className="text-zinc-600 text-xs mt-1">Nieuw saldo: {sporter.saldo + Number(credits)} credits</p>
          </div>
          <div>
            <label className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block">Omschrijving *</label>
            <input value={omschrijving} onChange={e => setOmschrijving(e.target.value)} placeholder="bv. Welkomstbonus, correctie..." className={inputCls} />
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold">Annuleren</button>
            <button type="submit" disabled={saving} data-testid="save-handmatig-credits-btn"
              className="flex-1 py-2.5 rounded-xl bg-[#C9A84C] text-black text-sm font-bold disabled:opacity-40">
              {saving ? "..." : "Opslaan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CreditsTab({ token }) {
  const [pakketten, setPakketten] = useState([]);
  const [sporters, setSporters] = useState([]);
  const [transacties, setTransacties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPakketModal, setShowPakketModal] = useState(false);
  const [editPakket, setEditPakket] = useState(null);
  const [creditsModal, setCreditsModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [activeView, setActiveView] = useState("pakketten");

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API}/api/schedule/creditpakketten`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/schedule/sporters-overzicht`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/schedule/credits/transacties`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (r1.ok) setPakketten((await r1.json()).pakketten || []);
      if (r2.ok) setSporters((await r2.json()).sporters || []);
      if (r3.ok) {
        const d = await r3.json();
        setTransacties([...(d.transacties || []), ...(d.orders || [])].sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
        ));
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleDeletePakket = async (id) => {
    if (!window.confirm("Pakket verwijderen?")) return;
    try {
      const r = await fetch(`${API}/api/schedule/creditpakketten/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error((await r.json()).detail);
      showToast("Pakket verwijderd");
      load();
    } catch (e) { showToast(e.message, "error"); }
  };

  const totalCredits = sporters.reduce((sum, s) => sum + (s.saldo || 0), 0);

  return (
    <div className="max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Betalingen</p>
          <h2 className="text-xl font-bold text-white">Credits</h2>
          <p className="text-zinc-500 text-sm mt-1">Creditpakketten en saldo per sporter</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { val: pakketten.filter(p => p.actief).length, label: "Actieve pakketten" },
          { val: sporters.length, label: "Sporters" },
          { val: totalCredits, label: "Totale credits uitstaan" },
        ].map(({ val, label }) => (
          <div key={label} className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4 text-center">
            <p className="text-2xl font-black text-white tabular-nums">{val}</p>
            <p className="text-zinc-600 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* View tabs */}
      <div className="flex gap-1 bg-[#0f0f0f] border border-white/5 rounded-xl p-1 w-fit mb-6">
        {[
          { key: "pakketten", label: "Pakketten" },
          { key: "sporters", label: "Sporters" },
          { key: "geschiedenis", label: "Geschiedenis" },
        ].map(v => (
          <button key={v.key} onClick={() => setActiveView(v.key)}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${activeView === v.key ? "text-black" : "text-zinc-400 hover:text-white"}`}
            style={activeView === v.key ? { background: "#C9A84C" } : {}}>
            {v.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-16 bg-[#0f0f0f] border border-white/5 rounded-2xl animate-pulse" />)}
        </div>
      ) : activeView === "pakketten" ? (
        <>
          <div className="flex justify-end mb-3">
            <button
              onClick={() => { setEditPakket(null); setShowPakketModal(true); }}
              data-testid="create-pakket-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-black font-bold text-sm"
              style={{ background: "#C9A84C" }}>
              <Plus size={14} /> Nieuw pakket
            </button>
          </div>
          {pakketten.length === 0 ? (
            <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-8 text-center">
              <CreditCard size={28} className="text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 font-semibold mb-1">Geen pakketten</p>
              <p className="text-zinc-600 text-sm">Maak creditpakketten aan die sporters kunnen kopen</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pakketten.map(p => (
                <div key={p.id} className={`bg-[#0f0f0f] border rounded-2xl p-4 flex items-center gap-4 ${p.actief ? "border-white/5" : "border-white/5 opacity-60"}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-[#C9A84C]/10">
                    <CreditCard size={20} className="text-[#C9A84C]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-bold text-sm">{p.naam}</p>
                      {!p.actief && <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-500">inactief</span>}
                    </div>
                    <p className="text-zinc-500 text-xs mt-0.5">
                      {p.credits} credits · €{Number(p.prijs).toFixed(2).replace(".", ",")}
                      {p.beschrijving && ` · ${p.beschrijving}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => { setEditPakket(p); setShowPakketModal(true); }}
                      data-testid={`edit-pakket-btn-${p.id}`}
                      className="p-2 rounded-xl border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-all">
                      <Edit size={13} />
                    </button>
                    <button onClick={() => handleDeletePakket(p.id)}
                      data-testid={`delete-pakket-btn-${p.id}`}
                      className="p-2 rounded-xl border border-red-500/20 text-red-400 hover:bg-red-500/8 transition-all">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : activeView === "sporters" ? (
        <>
          {sporters.length === 0 ? (
            <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-8 text-center">
              <Users size={28} className="text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 font-semibold">Geen sporters gevonden</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sporters.map(s => (
                <div key={s.id} className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
                    <span className="text-[#C9A84C] font-black text-sm">{(s.name || "?")[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{s.name}</p>
                    <p className="text-zinc-600 text-xs truncate">{s.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-sm font-black tabular-nums px-3 py-1 rounded-xl ${s.saldo > 0 ? "text-[#C9A84C] bg-[#C9A84C]/10" : "text-zinc-500 bg-white/5"}`}>
                      {s.saldo} cr.
                    </span>
                    <button
                      onClick={() => setCreditsModal(s)}
                      data-testid={`handmatig-credits-btn-${s.id}`}
                      className="p-2 rounded-xl border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-all">
                      <PlusCircle size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : activeView === "geschiedenis" ? (
        /* ── GESCHIEDENIS ── */
        <div data-testid="credits-geschiedenis">
          {transacties.length === 0 ? (
            <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-8 text-center">
              <History size={28} className="text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 font-semibold">Nog geen transacties</p>
            </div>
          ) : (
            <div className="space-y-2">
              {transacties.map((t, i) => {
                const isOrder = Boolean(t.order_id);
                const bedrag = t.credits || t.pakket_credits || 0;
                const naam = t.sporter_naam || t.sporter_email || t.sporter_id || "—";
                const datum = t.created_at ? new Date(t.created_at).toLocaleString("nl-NL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                return (
                  <div key={i} className="bg-[#0f0f0f] border border-white/5 rounded-xl p-3.5 flex items-center justify-between gap-3"
                    data-testid={`transactie-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${bedrag > 0 ? "bg-green-500/10" : "bg-red-500/10"}`}>
                        <span className={`text-xs font-black ${bedrag > 0 ? "text-green-400" : "text-red-400"}`}>{bedrag > 0 ? "+" : ""}{bedrag}</span>
                      </div>
                      <div>
                        <p className="text-white text-sm font-semibold">{naam}</p>
                        <p className="text-zinc-600 text-xs">{t.omschrijving || t.type || (isOrder ? `Pakket aankoop · ${t.status || ""}` : "Transactie")}</p>
                      </div>
                    </div>
                    <p className="text-zinc-500 text-xs shrink-0">{datum}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl text-sm font-semibold shadow-2xl ${toast.type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
          {toast.type === "success" ? <CheckCircle size={16} className="text-[#C9A84C]" /> : <AlertCircle size={16} className="text-red-400" />}
          {toast.msg}
        </div>
      )}

      {/* Modals */}
      {showPakketModal && (
        <PakketModal
          pakket={editPakket}
          token={token}
          onClose={() => { setShowPakketModal(false); setEditPakket(null); }}
          onSave={() => { setShowPakketModal(false); setEditPakket(null); load(); showToast(editPakket ? "Pakket bijgewerkt!" : "Pakket aangemaakt!"); }}
        />
      )}
      {creditsModal && (
        <HandmatigCreditsModal
          sporter={creditsModal}
          token={token}
          onClose={() => setCreditsModal(null)}
          onSave={(nieuwSaldo) => {
            setSporters(prev => prev.map(s => s.id === creditsModal.id ? { ...s, saldo: nieuwSaldo } : s));
            setCreditsModal(null);
            showToast("Credits aangepast!");
          }}
        />
      )}
    </div>
  );
}
